<ng-container *transloco="let t; prefix: 'reports'">
  <h2 class="reports__header">{{ t('title') }}</h2>

  <div class="reports__settings">
    <xi-dropdown
      [placeholder]="t('select-event-type')"
      [data]="eventTypes"
      select="single"
      [label]="t('event-type')"
      [(value)]="selectedEventType"
    ></xi-dropdown>

    <div>
      <label class="kxi-label kxi-normal-contrast" for="report-date-range">
        {{ t('date-range') }}
      </label>

      <xi-calendar
        id="report-date-range"
        placeholder="{{ t('calendar.placeholder') }}"
        [data]="calendarData"
        mode="relative"
        [checkedListId]="relativeRangeId"
        [popoutView]="true"
        [localization]="{
          firstInputPlaceholder: t('calendar.first-placeholder'),
          secondInputPlaceholder: t('calendar.second-placeholder'),
          startDate: t('calendar.start-date'),
          endDate: t('calendar.end-date'),
          errorString: t('calendar.error-string'),
          leftText: t('calendar.left-text'),
          rightText: t('calendar.right-text'),
          to: t('calendar.to'),
          custom: t('calendar.custom'),
          applyBtn: t('calendar.apply-button'),
          cancelBtn: t('calendar.cancel-button'),
          noSelection: t('calendar.no-selection'),
        }"
        (onValueChange)="onCalendarValueChange($event.detail)"
      ></xi-calendar>
    </div>

    <div class="reports__actions">
      <div class="xi-loader-button-container">
        <xi-default-button
          [text]="t('preview-report')"
          [disabled]="!canDoReport"
          (onClick)="previewReport()"
        ></xi-default-button>

        @if (isLoading) {
          <div class="xi-loader-container__inline-overlay">
            <xi-animation
              [height]="75"
              [width]="75"
              [loop]="true"
              animation="cx_loader"
            ></xi-animation>
          </div>
        }
      </div>

      <div class="xi-loader-button-container">
        <xi-default-button
          [text]="t('generate-report')"
          [disabled]="!canDoReport"
          (onClick)="generateReport()"
        ></xi-default-button>

        @if (isLoading) {
          <div class="xi-loader-container__inline-overlay">
            <xi-animation
              [height]="75"
              [width]="75"
              [loop]="true"
              animation="cx_loader"
            ></xi-animation>
          </div>
        }
      </div>
    </div>
  </div>

  @if (dataSource) {
    <div class="reports__content">
      <div class="reports__table-wrapper">
        <table mat-table [dataSource]="dataSource" multiTemplateDataRows class="xi-table__table">
          <ng-container matColumnDef="event">
            <th mat-header-cell *matHeaderCellDef>
              <div class="xi-table__table-header-content">
                <div class="xi-table__table-header-border"></div>
                {{ t('event') }}
              </div>
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.event }}
            </td>
          </ng-container>
          <ng-container matColumnDef="user">
            <th mat-header-cell *matHeaderCellDef>
              <div class="xi-table__table-header-content">
                <div class="xi-table__table-header-border"></div>
                {{ t('user') }}
              </div>
            </th>
            <td mat-cell *matCellDef="let element">
              @if (element.userId) {
                <span [class.ellipsis-loader]="!usersById.has(element.userId)">
                  {{ usersById.get(element.userId) }}
                  @if (usersById.has(element.userId) && !usersById.get(element.userId)) {
                    {{ t('not-found') }}
                  }
                </span>
              }
            </td>
          </ng-container>
          <ng-container matColumnDef="timestamp">
            <th mat-header-cell *matHeaderCellDef>
              <div class="xi-table__table-header-content">
                <div class="xi-table__table-header-border"></div>
                {{ t('timestamp') }}
              </div>
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.timestamp | lexDate: 'long' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="properties">
            <th mat-header-cell *matHeaderCellDef>
              <div class="xi-table__table-header-content">
                <div class="xi-table__table-header-border"></div>
                {{ t('properties') }}
              </div>
            </th>
            <td mat-cell *matCellDef="let element" (click)="$event.stopPropagation()">
              @if (expandedEvent !== element) {
                <a (click)="expandedEvent = element; $event.stopPropagation()"
                  >{{ t('properties-show') }}
                </a>
              }
              @if (expandedEvent === element) {
                <a (click)="expandedEvent = null; $event.stopPropagation()"
                  >{{ t('properties-hide') }}
                </a>
              }
            </td>
          </ng-container>
          <ng-container matColumnDef="expandedProperties">
            <td mat-cell *matCellDef="let element" [attr.colspan]="displayedColumns.length">
              <div class="reports__table-detail">
                <div class="reports__table-detail-rows">
                  @for (kv of element.properties | keyvalue; track kv) {
                    <div>{{ kv.key }}:</div>
                    <div>{{ kv.value }}</div>
                  }
                </div>
              </div>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns"
            class="reports__table-row"
          ></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: ['expandedProperties']"
            class="reports__table-detail-row"
            [class.reports__table-detail-row--expanded]="expandedEvent === row"
          ></tr>
          <tr class="xi-table__no-table-data" *matNoDataRow>
            <td [attr.colspan]="displayedColumns.length">
              @if (!isLoading) {
                <div>
                  <div>
                    <div class="xi-table__no-results-icon">
                      <xi-svg svg="no"></xi-svg>
                    </div>
                    <p class="xi-table__no-results-text">{{ t('no-events-found') }}</p>
                  </div>
                </div>
              }
            </td>
          </tr>
        </table>
      </div>
      @if (isLoading) {
        <div class="xi-loader-container__inline-overlay xi-loader-container__inline-overlay--blur">
          <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
        </div>
      }
    </div>
    <mat-paginator
      #paginator
      [length]="paging.count"
      [pageIndex]="paging.currentPage"
      [pageSize]="paging.pageSize"
      [pageSizeOptions]="[5, 10, 20, 50, 100]"
      [disabled]="isLoading"
      (page)="onPageChanged($event)"
      aria-label="Select page"
    ></mat-paginator>
  }
</ng-container>
